import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { ICompany } from '@nxsys/types/models/company';
import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import AppUploadFile from '@nxsys/components/AppUploadFile';
import { Form, Row, Col } from 'antd';
import { useImperativeHandle, Ref, useEffect, useState } from 'react';
import AppInputPassword from '@nxsys/components/AppInputPassword';

type RevenueEmployerSettingsFormRef = {
  submit: () => void;
};

export type RevenueEmployerSettingsFormValues = {
  employerRegistrationNumber: string;
  passwordP12: string;
  p12FileLink: File | string | null;
};

type RevenueEmployerSettingsFormProps = {
  companyData?: ICompany;
  formRef?: Ref<RevenueEmployerSettingsFormRef>;
  onSubmit?: (values: RevenueEmployerSettingsFormValues) => void;
};

const RevenueEmployerSettingsForm: React.FC<
  RevenueEmployerSettingsFormProps
> = ({ companyData, formRef, onSubmit }) => {
  const { messages } = useTypedIntl();
  const [form] = Form.useForm();
  const [needReinitializeForm, setNeedReinitializeForm] = useState(true);
  const watchValues = Form.useWatch([], form);

  useImperativeHandle(formRef, () => ({
    submit: () => {
      form.submit();
    },
  }));

  const onFinish = (values: any) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error('Failed:', errorInfo);
  };

  useEffect(() => {
    if (companyData && needReinitializeForm) {
      const initialValues = {
        employerRegistrationNumber:
          companyData.employerRegistrationNumber || '',
        passwordP12: companyData.passwordP12 || '',
        p12FileLink: companyData.p12FileLink || null,
      };
      form.setFieldsValue(initialValues);
      setNeedReinitializeForm(false);
    }
  }, [companyData]);

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      layout="vertical"
      labelCol={{ span: 24 }}
      wrapperCol={{ span: 24 }}
    >
      <Row gutter={24}>
        <Col sm={24} md={12}>
          <Form.Item
            label={
              messages['common.registrationNumber']
            }
            name="employerRegistrationNumber"
            rules={[
              {
                pattern: /^\d{7}[A-Za-z]{1,2}$/,
                message:
                  'Format: 7 digits + 1 or 2 letters (e.g. 1234567T or 1234567DH)',
              },
            ]}
          >
            <AppInput placeholder="e.g. 1234567T or 1234567DH" />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.passwordP12'] || 'Password P12'}
            name="passwordP12"
          >
            <AppInputPassword placeholder={messages['form.enter']} />
          </Form.Item>
        </Col>
        <Col sm={24} md={12}>
          <Form.Item
            label={messages['common.p12File'] || 'P12 File'}
            name="p12FileLink"
          >
            <AppUploadFile
              urlFile={
                typeof watchValues?.p12FileLink === 'string'
                  ? watchValues?.p12FileLink
                  : undefined
              }
              onChange={(file: File) => {
                form.setFieldValue('p12FileLink', file);
              }}
            />
          </Form.Item>
        </Col>
      </Row>
    </AppFormContainer>
  );
};

export default RevenueEmployerSettingsForm;
